interface BreadcrumbItem {
  label: string;
  path: string;
  isLast: boolean;
}

// Route-label map for the admin surface. Top-level keys are the first URL
// segment; values are the human-readable labels shown in the crumb trail.
const ADMIN_ROUTE_LABELS: Record<string, string> = {
  tenants: "Tenants",
  // CR-111: `catalog` top-level retired; `themes` is the new admin surface.
  themes: "Themes",
  "service-requests": "Service Requests",
  operators: "Operators",
  "system-config": "System Config",
};

/** Convert `foo-bar-baz` → `Foo Bar Baz`. */
function formatSlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

export function generateBreadcrumbItems(path: string): BreadcrumbItem[] {
  const normalizedPath = path.replace(/\/$/, "");
  const segments = normalizedPath.split("/").filter(Boolean);

  // Root or empty path → Home only.
  if (segments.length === 0) {
    return [{ label: "Home", path: "/", isLast: true }];
  }

  const firstSegment = segments[0];
  const topLabel = ADMIN_ROUTE_LABELS[firstSegment];

  if (!topLabel) {
    // Unknown routes collapse to Home as the only (last) crumb.
    return [{ label: "Home", path: "/", isLast: true }];
  }

  const crumbs: BreadcrumbItem[] = [
    { label: "Home", path: "/", isLast: false },
    {
      label: topLabel,
      path: `/${firstSegment}`,
      isLast: segments.length === 1,
    },
  ];

  // Sub-page under a known top-level admin route (e.g. /tenants/acme).
  if (segments.length >= 2) {
    const subPath = `/${segments.slice(0, 2).join("/")}`;
    crumbs.push({
      label: formatSlug(segments[1]),
      path: subPath,
      isLast: segments.length === 2,
    });
  }

  // Any further trailing segments collapse into a final label from the last slug.
  if (segments.length >= 3) {
    const tail = segments[segments.length - 1];
    const tailPath = `/${segments.join("/")}`;
    crumbs.push({ label: formatSlug(tail), path: tailPath, isLast: true });
  }

  return crumbs;
}
